import { Injectable } from '@angular/core';
import { Observable, timer, interval } from 'rxjs';
import { map, debounceTime, distinctUntilChanged, switchMap, filter } from 'rxjs/operators';

import { SyncStatus } from '../data';
import { SettingsState } from '../settings/service/reducer';
import { updateSettingsState, updateSettingsValue } from '../settings/service/actions';
import { LoginSelector } from '../settings/service/selectors';

import { BaseSyncService, SYNC_DEBOUNCE } from './sync.base';

@Injectable({ providedIn: 'root' })
export class SettingsSyncService extends BaseSyncService {
    entityType = 'settings';

    private readonly PERIODIC_SYNC_INTERVAL = 600000; // 10 minutes

    init() {
        // Monitor for dirty settings and automatically upload
        this.store
            .select((s) => s.entityChange?.settings)
            .pipe(
                distinctUntilChanged(),
                switchMap((status: SyncStatus) =>
                    this.store.pipe(
                        map((s) => ({
                            state: s.settings as SettingsState,
                            status
                        }))
                    )
                ),
                debounceTime(SYNC_DEBOUNCE)
            )
            .subscribe((settings) => {
                if (settings.status === 'Dirty') {
                    this.uploadSettings(settings.state);
                }
            });

        // Periodic download of settings every 10 minutes (only when logged in)
        this.store
            .select(LoginSelector)
            .pipe(
                map((loginState) => !!loginState.user && loginState.checked),
                distinctUntilChanged(),
                switchMap((loggedIn) => (loggedIn ? interval(this.PERIODIC_SYNC_INTERVAL) : timer(0, 0).pipe(filter(() => false))))
            )
            .subscribe(() => {
                this.downloadSettings();
            });
    }

    async sync(options?: { manual?: boolean }) {
        const manual = options?.manual ?? false;

        if (manual) {
            this.syncStatus$.next({ status: 'SYNCING' });
        }

        // Download settings from server
        this.downloadSettings();
    }

    uploadSettings(settings: any) {
        const settingsToUpload = structuredClone(settings);
        if (settingsToUpload.global?.login) {
            delete settingsToUpload.global.login;
        }

        if (settingsToUpload.data) {
            throw 'Oh noes!';
        }

        // Set status to syncing
        this.syncStatus$.next({ status: 'SYNCING' });
        this.start('settings');

        const headers = this.config.globalRequestHeaders;
        const promise: Observable<number> = this.httpClient.post(this.getSyncUrl(), settingsToUpload, {
            headers,
            requiresLogin: true
        });

        promise.pipe(this.handleSyncError()).subscribe((newTimestamp: number) => {
            const key = 'updatedAt';
            this.store.dispatch(updateSettingsValue({ gameId: 'global', settingsKey: key, newValue: newTimestamp }));
            this.complete('settings');
        });
    }

    downloadSettings() {
        // Set status to syncing
        this.syncStatus$.next({ status: 'SYNCING' });
        this.start('settings');

        const headers = this.config.globalRequestHeaders;
        this.httpClient
            .get(this.getSyncUrl(), { headers, requiresLogin: true })
            .pipe(
                this.handleSyncError(),
                map((serverData) => serverData?.[0]?.data)
            )
            .subscribe((serverData: SettingsState) => {
                if (serverData) {
                    if (serverData.global?.login) {
                        delete serverData.global.login;
                    }
                    if ((serverData as any).data) {
                        throw 'Oh noes!';
                    }
                    this.store.dispatch(updateSettingsState(serverData));
                }
                this.complete('settings');
            });
    }
}
